<template>
  <div class="room-tree-container">
    <el-tree ref="tree" :data="treeData" node-key="id" :props="defaultProps" :highlight-current="true"
      :expand-on-click-node="false" @node-click="handleNodeClick" :filter-node-method="_filterRoomNode">
      <span slot-scope="{ node, data }" class="custom-tree-node">
        <span class="tree-node-label">
          <i :class="data.icon" style="margin-right: 5px"></i>
          <span class="node-text">{{ node.label }}</span>
          <span v-if="data.ownerName && data.roomId" class="owner-name">{{ data.ownerName }}</span>
        </span>
      </span>
    </el-tree>
  </div>
</template>

<script>
import { queryUnits, queryRoomsTree } from '@/api/fee/meterWaterManageApi'
import { getCommunityId } from '@/api/community/communityApi'
import { queryReportOweFee } from '@/api/fee/listOweFeeApi'

export default {
  name: 'RoomTreeDiv',
  data() {
    return {
      treeData: [],
      defaultProps: {
        children: 'children',
        label: 'text'
      },
      communityId: '',
      lastSelected: {},
      isInitialized: false, // 添加初始化标志
      showOnlyOwe: false, // 是否仅显示欠费房屋
      oweRoomIds: new Set(), // 欠费房屋ID集合
      oweLoading: false, // 欠费数据加载中
      oweMap: {}, // 房间欠费金额映射 { roomId: { amount: number, loading: boolean } }
      originalTreeData: [], // 原始完整树数据副本
      skipAutoSelect: false // 跳过自动选择第一个房间
    }
  },
  created() {
    this.communityId = getCommunityId()
    this.loadTreeData()
  },
  methods: {
    async loadTreeData() {
      // 加载上次选择的房屋信息
      let lastSelected = localStorage.getItem('lastSelectedRoom');
      if (lastSelected) {
        try {
          this.lastSelected = JSON.parse(lastSelected);
        } catch (error) {
          console.error('解析lastSelected失败:', error);
          localStorage.removeItem('lastSelectedRoom');
        }
      }
      
      try {
        const units = await queryUnits({
          communityId: this.communityId
        })
        this.buildTreeData(units)
      } catch (error) {
        console.error('Failed to load tree data:', error)
        this.$message.error(this.$t('roomTree.loadError'))
      }
    },
    buildTreeData(units) {
      // 使用数组保持后端返回的排序顺序（楼栋按 seq 排序，单元按 unit_num 排序）
      const floorList = []
      const floorMap = {}

      units.forEach(unit => {
        if (!floorMap[unit.floorId]) {
          const floorNode = {
            id: `f_${unit.floorId}`,
            floorId: unit.floorId,
            floorNum: unit.floorNum,
            icon: "/img/floor.png",
            text: `${unit.floorNum}`,
            children: []
          }
          floorMap[unit.floorId] = floorNode
          floorList.push(floorNode)
        }
        // 单元保持后端 order by ff.seq, t.unit_num 的顺序
        floorMap[unit.floorId].children.push({
          id: `u_${unit.unitId}`,
          unitId: unit.unitId,
          unitNum: unit.unitNum,
          floorId: unit.floorId,
          icon: "/img/unit.png",
          text: `${unit.unitNum}`,
          children: []
        })
      })

      this.treeData = floorList

      // 保存原始完整树数据
      this.originalTreeData = JSON.parse(JSON.stringify(this.treeData))
      // 后台静默加载全量欠费数据到缓存
      this._loadAllOweData()
      this.$nextTick(() => {
        // 如果有上次选择的房屋，则展开并选择；否则选择第一个房屋
        if (this.lastSelected && this.lastSelected.floorId && this.lastSelected.unitId) {
          this.expandAndSelectLastRoom()
        } else {
          this.expandAndSelectFirstRoom()
        }
      })
    },
    // 通用排序：先按数字部分，再按字母部分
    // 支持格式：纯数字(1/2/10)、数字+字母(02A/3B)、文字+末尾数字(乐居亭1)
    _sortTreeNodes(nodes, key) {
      const parse = (str) => {
        const s = String(str)
        // 格式1: 数字+字母，如 "02A", "3B"
        const m1 = s.match(/^(\d+)([a-zA-Z]*)$/)
        if (m1) {
          return { num: parseInt(m1[1], 10), letter: m1[2] || '' }
        }
        // 格式2: 纯数字，如 "1", "10"
        if (/^\d+$/.test(s)) {
          return { num: parseInt(s, 10), letter: '' }
        }
        // 格式3: 末尾数字，如 "乐居亭1", "A栋3"
        const m2 = s.match(/(\d+)$/)
        if (m2) {
          return { num: parseInt(m2[1], 10), letter: '' }
        }
        return { num: 0, letter: s }
      }
      return (nodes || []).sort((a, b) => {
        const pa = parse(a[key])
        const pb = parse(b[key])
        if (pa.num !== pb.num) return pa.num - pb.num
        return pa.letter.localeCompare(pb.letter)
      })
    },
    // 新增：展开并选择上次选择的房屋
    async expandAndSelectLastRoom() {
      if (!this.lastSelected || !this.lastSelected.floorId || !this.lastSelected.unitId) {
        this.expandAndSelectFirstRoom()
        return
      }

      const treeRef = this.$refs.tree
      if (!treeRef || !treeRef.store) {
        // 如果树还没有完全初始化，延迟执行
        setTimeout(() => this.expandAndSelectLastRoom(), 100)
        return
      }

      try {
        // 查找对应的楼栋和单元
        const floorNode = this.treeData.find(floor => floor.floorId === this.lastSelected.floorId)
        if (!floorNode) {
          console.warn('未找到对应的楼栋:', this.lastSelected.floorId)
          this.expandAndSelectFirstRoom()
          return
        }

        const unitNode = floorNode.children.find(unit => unit.unitId === this.lastSelected.unitId)
        if (!unitNode) {
          console.warn('未找到对应的单元:', this.lastSelected.unitId)
          this.expandAndSelectFirstRoom()
          return
        }

        // 展开楼栋和单元
        const floorTreeNode = treeRef.store.nodesMap[floorNode.id]
        const unitTreeNode = treeRef.store.nodesMap[unitNode.id]
        
        if (floorTreeNode) {
          floorTreeNode.expanded = true
        }
        if (unitTreeNode) {
          unitTreeNode.expanded = true
        }

          // 加载房屋数据（标记跳过自动选择，由 selectSpecificRoom 手动选择）
        this.skipAutoSelect = true
        await this.loadRooms(unitNode, { data: unitNode })
        this.skipAutoSelect = false

        // 选择上次选择的房屋
        if (this.lastSelected.roomId) {
          this.selectSpecificRoom(this.lastSelected.roomId)
        }

        this.isInitialized = true
      } catch (error) {
        console.error('展开上次选择的房屋失败:', error)
        this.expandAndSelectFirstRoom()
      }
    },
    // 新增：选择特定的房屋
    selectSpecificRoom(roomId) {
      const treeRef = this.$refs.tree
      if (!treeRef) return

      const roomNodeId = `r_${roomId}`
      const roomTreeNode = treeRef.store.nodesMap[roomNodeId]
      
      if (roomTreeNode) {
        // 设置当前选中节点
        treeRef.setCurrentKey(roomNodeId)
        
        // 触发选择事件
        this.$emit('selectRoom', {
          roomId: roomId,
          roomName: this.lastSelected.roomName || ''
        })
      } else {
        // 如果房屋节点还没有加载，等待加载完成后再选择
        setTimeout(() => this.selectSpecificRoom(roomId), 200)
      }
    },
    async expandAndSelectFirstRoom() {
      if (!this.treeData.length) return
      
      const firstFloor = this.treeData[0]
      if (!firstFloor.children || !firstFloor.children.length) return
      const firstUnit = firstFloor.children[0]
      const treeRef = this.$refs.tree
      if (treeRef && treeRef.store) {
        treeRef.store.nodesMap[firstFloor.id] && (treeRef.store.nodesMap[firstFloor.id].expanded = true)
        treeRef.store.nodesMap[firstUnit.id] && (treeRef.store.nodesMap[firstUnit.id].expanded = true)
      }
      await this.loadRooms(firstUnit, { data: firstUnit })
      this.isInitialized = true
    },
    async handleNodeClick(data, node) {
      if (data.id.startsWith('f_')) {
        // 点击楼栋：自动展开并加载第一个单元
        if (!node.expanded) {
          node.expanded = true
        }
        if (data.children && data.children.length > 0) {
          const firstUnit = data.children[0]
          const treeRef = this.$refs.tree
          const unitTreeNode = treeRef && treeRef.store ? treeRef.store.nodesMap[firstUnit.id] : null
          if (unitTreeNode && !unitTreeNode.expanded) {
            unitTreeNode.expanded = true
            await this.loadRooms(firstUnit, { data: firstUnit })
          }
        }
      } else if (data.id.startsWith('u_')) {
        if (!node.expanded) {
          await this.loadRooms(data, node)
          node.expanded = true
        }
      } else if (data.id.startsWith('r_')) {
        // 获取父节点信息
        const parentNodes = this.getParentNodes(data)
        let selectedData = {
          floorId: parentNodes.floorId,
          unitId: parentNodes.unitId,
          roomId: data.roomId,
          roomName: data.roomName
        };
        
        // 保存到localStorage
        localStorage.setItem('lastSelectedRoom', JSON.stringify(selectedData));
        this.lastSelected = selectedData;

        this.$emit('selectRoom', {
          roomId: data.roomId,
          roomName: data.roomName
        })
      }
    },
    // 新增：获取父节点信息
    getParentNodes(data) {
      const treeRef = this.$refs.tree
      if (!treeRef || !treeRef.store) return { floorId: '', unitId: '' }

      const node = treeRef.store.nodesMap[data.id]
      if (!node || !node.parent) return { floorId: '', unitId: '' }

      const unitNode = node.parent
      const floorNode = unitNode.parent

      return {
        floorId: floorNode ? floorNode.data.floorId : '',
        unitId: unitNode ? unitNode.data.unitId : ''
      }
    },
    async loadRooms(unitData, node) {
      try {
        const { rooms } = await queryRoomsTree({
          unitId: unitData.unitId,
          communityId: this.communityId,
          page: 1,
          row: 1000
        })

        if (rooms && rooms.length > 0) {
          let filteredRooms = rooms
          // 如果开启了仅显示欠费，过滤出欠费房屋
          if (this.showOnlyOwe && this.oweRoomIds.size > 0) {
            filteredRooms = rooms.filter(room => this.oweRoomIds.has(room.roomId))
          }

          const roomNodes = this._sortTreeNodes(filteredRooms.map(room => ({
            id: `r_${room.roomId}`,
            roomId: room.roomId,
            roomName: `${room.floorNum}-${room.unitNum}-${room.roomNum}`,
            icon: "/img/room.png",
            text: `${room.roomNum}`,
            ownerName: room.ownerName || ''
          })), 'text')

          // Update the node's children
          this.$set(node.data, 'children', roomNodes)
          
          // 异步加载本单元所有房间的欠费金额
          const roomIds = filteredRooms.map(r => r.roomId)
          if (roomIds.length > 0) {
            this._fetchOweForRooms(roomIds)
          }

          // 只有在初始化时才自动选择第一个房屋（跳过由 expandAndSelectLastRoom 触发的加载）
          if (!this.isInitialized && !this.skipAutoSelect && roomNodes.length > 0) {
            this.$emit('selectRoom', {
              roomId: roomNodes[0].roomId,
              roomName: roomNodes[0].roomName
            })
          }
        } else {
          this.$set(node.data, 'children', [])
        }
      } catch (error) {
        console.error('Failed to load rooms:', error)
        this.$message.error(this.$t('roomTree.loadRoomError'))
      }
    },
    // 新增：清除保存的选择记录
    clearLastSelected() {
      localStorage.removeItem('lastSelectedRoom')
      this.lastSelected = {}
    },
    // 欠费筛选开关变化
    async _onOweFilterChange(val) {
      if (val) {
        // 开启筛选：加载欠费房屋ID列表，然后扫描所有单元构建过滤树
        await this._loadOweRoomIds()
        await this._scanAndFilterTree()
      } else {
        // 关闭筛选：恢复原始完整树（保留 oweRoomIds 供下次切换复用）
        this.treeData = JSON.parse(JSON.stringify(this.originalTreeData))
        this.isInitialized = false
        this.$nextTick(() => {
          this.expandAndSelectFirstRoom()
        })
      }
    },
    // 加载全量欠费数据到缓存（页面初始化时调用一次）
    async _loadAllOweData() {
      if (this.oweLoading) return
      this.oweLoading = true
      try {
        const { data } = await queryReportOweFee({
          communityId: this.communityId,
          page: 1,
          row: 10000
        })
        if (data && data.length > 0) {
          data.forEach(item => {
            if (item.payerObjId) {
              const amount = parseFloat(item.amountOwed || 0) || 0
              this.$set(this.oweMap, item.payerObjId, {
                amount: amount,
                loading: false
              })
              // 记录真正有欠费的房间ID（amount > 0）
              if (amount > 0) {
                this.oweRoomIds.add(item.payerObjId)
              }
            }
          })
        }
      } catch (error) {
        console.error('加载欠费数据失败:', error)
      }
      this.oweLoading = false
    },
    // 加载所有欠费房屋ID和金额（供"仅欠费"模式使用）
    async _loadOweRoomIds() {
      // 如果 oweMap 已缓存，直接复用
      if (Object.keys(this.oweMap).length > 0 && this.oweRoomIds.size > 0) {
        // 已经通过 _loadAllOweData 加载过
        return
      }
      await this._loadAllOweData()
    },
    // 扫描所有单元，构建只包含有欠费房屋的树
    async _scanAndFilterTree() {
      if (this.oweRoomIds.size === 0 || !this.originalTreeData.length) {
        this.treeData = []
        return
      }

      // 收集所有单元
      const allUnits = []
      this.originalTreeData.forEach(floor => {
        floor.children.forEach(unit => {
          allUnits.push({ floor, unit })
        })
      })

      // 并发加载每个单元的房间（限制并发数避免请求过多）
      const concurrency = 6
      const results = []
      for (let i = 0; i < allUnits.length; i += concurrency) {
        const batch = allUnits.slice(i, i + concurrency)
        const batchResults = await Promise.all(
          batch.map(async ({ floor, unit }) => {
            try {
              const res = await queryRoomsTree({
                unitId: unit.unitId,
                communityId: this.communityId,
                page: 1,
                row: 1000
              })
              const rooms = res.rooms || []
              const hasOwe = rooms.some(r => this.oweRoomIds.has(r.roomId))
              return { floor, unit, hasOwe }
            } catch (e) {
              return { floor, unit, hasOwe: false }
            }
          })
        )
        results.push(...batchResults)
      }

      // 构建过滤后的树，保持原始楼栋顺序
      const floorList = []
      const floorMap = {}
      results.forEach(({ floor, unit, hasOwe }) => {
        if (!hasOwe) return
        if (!floorMap[floor.floorId]) {
          const fn = { ...floor, children: [] }
          floorMap[floor.floorId] = fn
          floorList.push(fn)
        }
        floorMap[floor.floorId].children.push({ ...unit, children: [] })
      })

      this.treeData = floorList
      this.isInitialized = false
      this.$nextTick(() => {
        this.expandAndSelectFirstRoom()
      })
    },
    // 刷新所有已展开单元的房屋数据
    _refreshAllExpandedUnits() {
      const treeRef = this.$refs.tree
      if (!treeRef || !treeRef.store) return

      const nodesMap = treeRef.store.nodesMap
      const unitNodes = Object.values(nodesMap).filter(
        n => n.data && n.data.id && n.data.id.startsWith('u_') && n.expanded
      )

      unitNodes.forEach(unitNode => {
        this.loadRooms(unitNode.data, unitNode)
      })
    },
    // 获取房间欠费金额
    _oweAmount(roomId) {
      const item = this.oweMap[roomId]
      return item ? (item.amount || 0) : 0
    },
    // 检查欠费是否加载中
    _oweLoading(roomId) {
      const item = this.oweMap[roomId]
      return item ? !!item.loading : false
    },
    // 格式化欠费金额
    _formatOweAmount(roomId) {
      const amount = this._oweAmount(roomId)
      return amount.toFixed(2)
    },
    // 标记房间欠费数据加载中（实际数据由 _loadAllOweData 全局缓存）
    _fetchOweForRooms(roomIds) {
      if (!roomIds || roomIds.length === 0) return

      // 如果全局缓存已加载完成，数据已就绪，无需额外操作
      if (this.oweMap && Object.keys(this.oweMap).length > 0) {
        // 确保这些房间在 oweMap 中有记录（可能在缓存加载时已包含，也可能需要标记为0）
        roomIds.forEach(id => {
          if (!this.oweMap[id]) {
            this.$set(this.oweMap, id, { amount: 0, loading: false })
          }
        })
        return
      }

      // 缓存尚未加载完成，标记为加载中，等待 _loadAllOweData 完成后自动更新
      roomIds.forEach(id => {
        if (!this.oweMap[id]) {
          this.$set(this.oweMap, id, { amount: 0, loading: true })
        }
      })
    },
    // 树筛选方法：隐藏没有欠费房屋的单元和楼层
    _filterRoomNode(value, data) {
      // 如果没开启筛选，全部显示
      if (!this.showOnlyOwe || this.oweRoomIds.size === 0) return true
      // 房间节点：检查是否在欠费列表中
      if (data.id && data.id.startsWith('r_')) {
        return this.oweRoomIds.has(data.roomId)
      }
      // 单元和楼层节点：默认显示（让用户手动展开查看）
      if (data.id && (data.id.startsWith('u_') || data.id.startsWith('f_'))) {
        return true
      }
      return true
    }
  }
}
</script>

<style lang="scss" scoped>
.room-tree-container {
  height: 100%;
  overflow-y: auto;
  padding: 10px;

  .tree-filter-bar {
    margin-bottom: 8px;
    padding: 4px 0;
    border-bottom: 1px solid #ebeef5;
    display: flex;
    align-items: center;
    justify-content: flex-start;
  }

  .custom-tree-node {
    flex: 1;
    display: flex;
    align-items: center;
    font-size: 13px;
    padding: 4px 0;
    line-height: 1.4;

    .tree-node-label {
      flex: 1;
      min-width: 0;
      display: flex;
      align-items: center;
      gap: 2px;

      .node-text {
        white-space: nowrap;
        font-weight: 500;
      }

      .owner-name {
        font-size: 11px;
        color: #909399;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
        margin-left: 2px;
      }
    }

    .tree-node-owe {
      flex-shrink: 0;
      margin-left: 6px;

      .owe-spinner {
        font-size: 12px;
        color: #c0c4cc;
      }

      .owe-badge {
        display: inline-block;
        font-size: 11px;
        color: #f56c6c;
        background: #fef0f0;
        border: 1px solid #fde2e2;
        border-radius: 3px;
        padding: 0 4px;
        line-height: 18px;
        font-weight: 600;
        white-space: nowrap;
      }
    }
  }
}
</style>